/**
 * Hook for Lost Loans data and operations
 *
 * Provides:
 * - Fetching lost loans with pagination and filtering
 * - Setting loss reasons (single and bulk)
 * - Metrics breakdown by reason
 */

import { useState, useCallback, useRef } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

/**
 * Disposition recorded against a discharged loan in the per-loan
 * `lost_loan_reasons` store. Every value is a LOSS (the loan was discharged
 * without being refinanced internally) EXCEPT the retained dispositions in
 * RETAINED_LOSS_REASONS below, which mean the client stayed with the firm.
 */
export type LossReason =
  | 'bridging_completed'
  | 'property_sold'
  | 'lost_to_competitor'
  | 'paid_off'
  | 'debt_consolidation'
  | 'investment_sold'
  | 'relationship_settlement'
  | 'customer_moved'
  | 'deceased'
  | 'uncontactable'
  | 'refinanced_in_house'
  | 'unknown';

/**
 * Display labels for loss reasons
 */
export const LOSS_REASON_LABELS: Record<LossReason, string> = {
  bridging_completed: 'Bridging Loan Completed',
  property_sold: 'Property Sold',
  lost_to_competitor: 'Lost to Competitor',
  paid_off: 'Loan Paid Off',
  debt_consolidation: 'Debt Consolidation',
  investment_sold: 'Investment Property Sold',
  relationship_settlement: 'Relationship Settlement',
  customer_moved: 'Customer Moved Banks',
  deceased: 'Deceased / Estate',
  uncontactable: 'Uncontactable',
  refinanced_in_house: 'Refinanced In-House',
  unknown: 'Unknown',
};

/**
 * UI color themes for loss reasons
 */
export const LOSS_REASON_COLORS: Record<LossReason, { bg: string; text: string; border: string }> = {
  bridging_completed: { bg: 'bg-blue-900/30', text: 'text-blue-400', border: 'border-blue-500/30' },
  property_sold: { bg: 'bg-purple-900/30', text: 'text-purple-400', border: 'border-purple-500/30' },
  lost_to_competitor: { bg: 'bg-red-900/30', text: 'text-red-400', border: 'border-red-500/30' },
  paid_off: { bg: 'bg-green-900/30', text: 'text-green-400', border: 'border-green-500/30' },
  debt_consolidation: { bg: 'bg-orange-900/30', text: 'text-orange-400', border: 'border-orange-500/30' },
  investment_sold: { bg: 'bg-purple-900/30', text: 'text-purple-400', border: 'border-purple-500/30' },
  relationship_settlement: { bg: 'bg-gray-700', text: 'text-gray-400', border: 'border-gray-600' },
  customer_moved: { bg: 'bg-yellow-900/30', text: 'text-yellow-400', border: 'border-yellow-500/30' },
  deceased: { bg: 'bg-gray-700', text: 'text-gray-400', border: 'border-gray-600' },
  uncontactable: { bg: 'bg-amber-900/30', text: 'text-amber-400', border: 'border-amber-500/30' },
  refinanced_in_house: { bg: 'bg-emerald-900/30', text: 'text-emerald-400', border: 'border-emerald-500/30' },
  unknown: { bg: 'bg-gray-700', text: 'text-gray-400', border: 'border-gray-600' },
};

/**
 * All loss reasons in display order. Use this for DISPLAY and FILTER surfaces
 * (stat cards, filter dropdowns) so already-set values always render and filter.
 * For WRITE dropdowns use STANDARD_LOSS_REASONS instead (see below).
 */
export const ALL_LOSS_REASONS: LossReason[] = [
  'lost_to_competitor',
  'property_sold',
  'bridging_completed',
  'paid_off',
  'debt_consolidation',
  'investment_sold',
  'relationship_settlement',
  'customer_moved',
  'deceased',
  'uncontactable',
  'refinanced_in_house',
  'unknown',
];

/**
 * RETAINED dispositions: the loan is off the book but the client was NOT lost.
 * 'refinanced_in_house' = the firm refinanced the client itself, so the old
 * loan stopped paying trail. Anything that counts, totals or takes a percentage
 * of LOSSES must exclude these (see splitLossMetrics) and may show them as a
 * separate retained bucket. Mirrors WATCHLIST_ONLY_LOSS_REASONS in
 * services/client-functions/src/refinance/setLossReasonSchema.ts.
 */
export const RETAINED_LOSS_REASONS: LossReason[] = ['refinanced_in_house'];

/** True when a stored reason value is a retained (non-loss) disposition. */
export const isRetainedLossReason = (reason: string | null | undefined): boolean =>
  reason != null && (RETAINED_LOSS_REASONS as string[]).includes(reason);

/**
 * Freely-selectable loss reasons for GENERIC write dropdowns (BulkActionBar,
 * LostLoanCard). Excludes 'uncontactable', which is a gated disposition: it is
 * only writable on the Discharged Loans per-loan editor once the re-engagement
 * attempt bar is met, and the server (setLossReason) enforces that gate plus
 * truly-discharged membership. Never add 'uncontactable' to a generic write list.
 * Also excludes the retained dispositions: setLossReason and resolveDormantLoan
 * reject them, because those surfaces record an internal refinance through the
 * refinance-match flow instead.
 */
export const STANDARD_LOSS_REASONS: LossReason[] = ALL_LOSS_REASONS.filter(
  (r) => r !== 'uncontactable' && !RETAINED_LOSS_REASONS.includes(r),
);

/**
 * Reasons offered when confirming a discharge on the Discharge Watch-List: the
 * standard losses plus the retained dispositions, retained FIRST so "we kept
 * this client" is not buried under ten ways of losing one. The watch list is
 * the only surface that can write a retained disposition (resolveWatchlistLoan),
 * because it has no successor loan to link: on a snapshot-style statement the
 * new loan may not have appeared yet.
 */
export const WATCHLIST_DISCHARGE_REASONS: LossReason[] = [
  ...RETAINED_LOSS_REASONS,
  ...STANDARD_LOSS_REASONS,
];

/**
 * Split a per-reason breakdown into LOSSES and RETAINED. The single definition
 * of "how many loans were lost" for any surface that renders loss statistics.
 *
 * With no retained rows this returns the input totals untouched, so a tenant
 * that never records a retained disposition sees exactly what it saw before.
 * When retained rows exist, the lost totals are rebuilt from the per-reason
 * figures (integer cents, no rounding) with the retained reasons left out.
 */
export function splitLossMetrics(metrics: LostLoansMetrics): {
  lostTotal: number;
  lostValueCents: number;
  retainedTotal: number;
  retainedValueCents: number;
} {
  let retainedTotal = 0;
  let retainedValueCents = 0;
  RETAINED_LOSS_REASONS.forEach((r) => {
    retainedTotal += metrics.byReason[r] || 0;
    retainedValueCents += metrics.byReasonValueCents[r] || 0;
  });
  if (retainedTotal === 0 && retainedValueCents === 0) {
    return {
      lostTotal: metrics.total,
      lostValueCents: metrics.totalValueCents,
      retainedTotal: 0,
      retainedValueCents: 0,
    };
  }
  let lostTotal = 0;
  let lostValueCents = 0;
  ALL_LOSS_REASONS.forEach((r) => {
    if (RETAINED_LOSS_REASONS.includes(r)) return;
    lostTotal += metrics.byReason[r] || 0;
    lostValueCents += metrics.byReasonValueCents[r] || 0;
  });
  return { lostTotal, lostValueCents, retainedTotal, retainedValueCents };
}

/**
 * A discharged loan that was NOT refinanced internally
 */
export interface LostLoan {
  loanId: string;
  customerName: string;
  normalizedName: string;
  lender: string | null;
  dischargeDate: string;
  balanceAtDischargeCents: number | null;
  brokerId: string;
  brokerDisplay: string | null;
  officeId: string | null;
  officeName: string | null;
  templateId: string;
  templateName: string | null;
  settlementDate: string | null;
  monthsActive: number | null;
  lossReason: LossReason | null;
  lossReasonSetByUid: string | null;
  lossReasonSetByEmail: string | null;
  lossReasonSetAt: string | null;
  lossNotes: string | null;
}

/**
 * Aggregated metrics for lost loans
 */
export interface LostLoansMetrics {
  total: number;
  totalValueCents: number;
  byReason: Record<LossReason, number>;
  byReasonValueCents: Record<LossReason, number>;
}

export type CategorizationStatus = 'all' | 'needs_categorization' | 'completed';

export interface FetchLostLoansOptions {
  companyId: string;
  acquiredTemplateId: string;
  officeId?: string;
  brokerId?: string;
  categorizationStatus?: CategorizationStatus;
  lossReason?: LossReason | 'all';
  sortBy?: 'newest' | 'oldest' | 'balance_high' | 'balance_low';
  limit?: number;
  offset?: number;
  entityCode?: string;
}

export interface SetLossReasonOptions {
  companyId: string;
  templateId?: string;  // Resolved fileTemplate ID (unified: via resolveLoanBookSelection; legacy: raw selection)
  acquiredEntityCode?: string;  // Acquired book's entity binding (unified mode) — mirrors the read path's entityCode
  loanIds: string[];
  reason: LossReason;
  notes?: string | null;
}

export interface UseLostLoansReturn {
  loans: LostLoan[];
  metrics: LostLoansMetrics | null;
  total: number;
  hasMore: boolean;
  isLoading: boolean;
  isSaving: boolean;
  error: Error | null;
  fetchLostLoans: (options: FetchLostLoansOptions) => Promise<void>;
  loadMore: () => Promise<void>;
  setLossReason: (options: SetLossReasonOptions) => Promise<boolean>;
  /** Server-side message from the most recent setLossReason failure (e.g. "notes: String must contain at most 2000 character(s)"), or null. Read synchronously after a false return to show WHY the save failed. */
  lastSaveError: () => string | null;
  clearLoans: () => void;
  // Selection state for bulk operations
  selectedLoanIds: Set<string>;
  toggleLoanSelection: (loanId: string) => void;
  selectAllLoans: () => void;
  clearSelection: () => void;
}

/**
 * Hook to fetch and manage lost loans
 */
export function useLostLoans(): UseLostLoansReturn {
  const [loans, setLoans] = useState<LostLoan[]>([]);
  const [metrics, setMetrics] = useState<LostLoansMetrics | null>(null);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  // Synchronous holder for the last save failure's server message. A ref (not
  // state) so callers can read it immediately after `await setLossReason(...)`
  // returns false, without waiting for a re-render.
  const lastSaveErrorRef = useRef<string | null>(null);
  const [lastOptions, setLastOptions] = useState<FetchLostLoansOptions | null>(null);
  const [selectedLoanIds, setSelectedLoanIds] = useState<Set<string>>(new Set());

  const fetchLostLoans = useCallback(async (options: FetchLostLoansOptions): Promise<void> => {
    const { companyId, acquiredTemplateId } = options;

    if (!companyId || !acquiredTemplateId) {
      setError(new Error('Company ID and Acquired Template ID are required'));
      return;
    }

    setIsLoading(true);
    setError(null);
    setLastOptions(options);

    try {
      const getLostLoansFn = httpsCallable<
        FetchLostLoansOptions,
        { success: boolean; data: { loans: LostLoan[]; metrics: LostLoansMetrics; total: number; hasMore: boolean } }
      >(functions, 'getLostLoans');

      const result = await getLostLoansFn(options);

      if (result.data.success) {
        setLoans(result.data.data.loans);
        setMetrics(result.data.data.metrics);
        setTotal(result.data.data.total);
        setHasMore(result.data.data.hasMore);
        setSelectedLoanIds(new Set()); // Clear selection on new fetch
      } else {
        throw new Error('Failed to fetch lost loans');
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error fetching lost loans');
      setError(error);
      console.error('[useLostLoans] Error:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadMore = useCallback(async (): Promise<void> => {
    if (!lastOptions || !hasMore || isLoading) return;

    setIsLoading(true);

    try {
      const getLostLoansFn = httpsCallable<
        FetchLostLoansOptions,
        { success: boolean; data: { loans: LostLoan[]; metrics: LostLoansMetrics; total: number; hasMore: boolean } }
      >(functions, 'getLostLoans');

      const newOptions: FetchLostLoansOptions = {
        ...lastOptions,
        offset: loans.length,
      };

      const result = await getLostLoansFn(newOptions);

      if (result.data.success) {
        setLoans(prev => [...prev, ...result.data.data.loans]);
        setHasMore(result.data.data.hasMore);
        setLastOptions(newOptions);
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error loading more loans');
      setError(error);
      console.error('[useLostLoans] Error loading more:', error);
    } finally {
      setIsLoading(false);
    }
  }, [lastOptions, hasMore, isLoading, loans.length]);

  const setLossReasonFn = useCallback(async (options: SetLossReasonOptions): Promise<boolean> => {
    const { companyId, templateId, acquiredEntityCode, loanIds, reason, notes } = options;

    if (!companyId || loanIds.length === 0) {
      setError(new Error('Company ID and at least one Loan ID are required'));
      return false;
    }

    // OPTIMISTIC UPDATE: Immediately update UI before API call
    // This provides instant feedback to the user
    const previousLoans = [...loans];
    const previousMetrics = metrics ? { ...metrics } : null;
    const previousTotal = total;

    // If filtering by 'needs_categorization', remove the loans from view immediately
    // Otherwise, update their reason in place
    const isFilteringNeedsCategorization = lastOptions?.categorizationStatus === 'needs_categorization';

    if (isFilteringNeedsCategorization) {
      // Remove loans from list (they'll appear in 'completed' filter)
      setLoans(prev => prev.filter(loan => !loanIds.includes(loan.loanId)));
      setTotal(prev => Math.max(0, prev - loanIds.length));
    } else {
      // Update loans in place
      setLoans(prev =>
        prev.map(loan =>
          loanIds.includes(loan.loanId)
            ? {
                ...loan,
                lossReason: reason,
                lossNotes: notes || null,
                lossReasonSetAt: new Date().toISOString(),
              }
            : loan
        )
      );
    }

    // Update metrics optimistically
    if (metrics) {
      const affectedLoans = previousLoans.filter(l => loanIds.includes(l.loanId));
      const affectedValueCents = affectedLoans.reduce(
        (sum, l) => sum + (l.balanceAtDischargeCents || 0),
        0
      );

      setMetrics(prev => {
        if (!prev) return prev;
        const updated = { ...prev };

        // Decrease count for old reasons (null = uncategorized)
        for (const loan of affectedLoans) {
          const oldReason = loan.lossReason || 'unknown';
          updated.byReason[oldReason] = Math.max(0, (updated.byReason[oldReason] || 0) - 1);
          updated.byReasonValueCents[oldReason] = Math.max(0, (updated.byReasonValueCents[oldReason] || 0) - (loan.balanceAtDischargeCents || 0));
        }

        // Increase count for new reason
        updated.byReason[reason] = (updated.byReason[reason] || 0) + affectedLoans.length;
        updated.byReasonValueCents[reason] = (updated.byReasonValueCents[reason] || 0) + affectedValueCents;

        return updated;
      });
    }

    // Clear selection immediately
    setSelectedLoanIds(new Set());

    // Now make the API call in background
    setIsSaving(true);
    setError(null);

    try {
      const setLossReasonCall = httpsCallable<
        SetLossReasonOptions,
        { success: boolean; updatedCount: number }
      >(functions, 'setLossReason');

      // Build the payload WITHOUT `templateId`/`acquiredEntityCode` keys when absent.
      // The Firebase callable serializer encodes a present `undefined` property as
      // `null` on the wire (undefined == null), and the strict server schema would
      // reject that null. Omitting the key avoids sending null at all. (The backend
      // also coalesces nullish/blank templateId to undefined as a second line of
      // defence.)
      const payload: SetLossReasonOptions = { companyId, loanIds, reason, notes };
      if (templateId) payload.templateId = templateId;
      if (acquiredEntityCode) payload.acquiredEntityCode = acquiredEntityCode;
      const result = await setLossReasonCall(payload);

      if (result.data.success) {
        // Success! Optimistic update was correct, nothing more to do
        lastSaveErrorRef.current = null;
        return true;
      } else {
        throw new Error('Failed to set loss reason');
      }
    } catch (err) {
      // REVERT optimistic update on error
      setLoans(previousLoans);
      setMetrics(previousMetrics);
      setTotal(previousTotal);

      const error = err instanceof Error ? err : new Error('Unknown error setting loss reason');
      setError(error);
      lastSaveErrorRef.current = error.message || null;
      console.error('[useLostLoans] Error setting reason:', error);
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [lastOptions, loans, metrics, total]);

  const clearLoans = useCallback(() => {
    setLoans([]);
    setMetrics(null);
    setTotal(0);
    setHasMore(false);
    setError(null);
    setLastOptions(null);
    setSelectedLoanIds(new Set());
  }, []);

  // Selection handlers for bulk operations
  const toggleLoanSelection = useCallback((loanId: string) => {
    setSelectedLoanIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(loanId)) {
        newSet.delete(loanId);
      } else {
        newSet.add(loanId);
      }
      return newSet;
    });
  }, []);

  const selectAllLoans = useCallback(() => {
    setSelectedLoanIds(new Set(loans.map(l => l.loanId)));
  }, [loans]);

  const clearSelection = useCallback(() => {
    setSelectedLoanIds(new Set());
  }, []);

  return {
    loans,
    metrics,
    total,
    hasMore,
    isLoading,
    isSaving,
    error,
    fetchLostLoans,
    loadMore,
    setLossReason: setLossReasonFn,
    lastSaveError: () => lastSaveErrorRef.current,
    clearLoans,
    selectedLoanIds,
    toggleLoanSelection,
    selectAllLoans,
    clearSelection,
  };
}
